import { useState, useRef } from 'react'
import { Button, Form } from 'react-bootstrap'
import { Redirect } from 'react-router'
import { useParams } from 'react-router-dom/cjs/react-router-dom.min'
import { UPLOAD_URL } from '../Api'
import ErrorWindow from '../Indicators/ErrorWindow'
import LoadingIndicator from '../Indicators/LoadingIndicator'
import { APP_DIRECTORY } from '../SystemVariables'
import './UploadSound.css'

const MEGABYTE = 1048576

const submit = (soundFile, iconFile, slotNumber, accessToken, onSubmitComplete, onSubmitError, setSubmitting) => {
    setSubmitting(true)
    const data = new FormData()
    data.append('soundFile', soundFile)
    data.append('iconFile', iconFile)
    data.append('slotName', slotNumber)
    fetch(UPLOAD_URL, {
        method: 'post',
        headers: {'Authorization':`Bearer ${accessToken}`},
        body: data
    })
    .then(response=>{
        if(!response.ok) {
            const error = new Error(`Network response was not ok: ${response.status}`)
            error.response = response
            throw error
        } else {
            return response.json()
        }
    }).then(()=>{
        onSubmitComplete()
    })
    .catch((err)=>{
        onSubmitError(err)
    })
}


const verifyFileSize = (file, megaBytes) => {
    const sizeInBytes = file.size
    const maxFileSize = MEGABYTE * megaBytes
    return (sizeInBytes <= maxFileSize)
}


const getHumanReadableMaxFileSize = (megaBytes) => {
    const maxFileSize = MEGABYTE * megaBytes
    return maxFileSize / 1048576
}


const UploadFile = (props) => {
    const { slotNumber } = useParams()
    const [soundFile, setSoundFile] = useState(undefined)
    const [iconFile, setIconFile] = useState(undefined)
    const [redirect, setRedirect] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState(undefined)
    const soundFileRef = useRef(null)
    soundFileRef.current = soundFile
    if(!props.accessToken) {
        return(<Redirect to={`${APP_DIRECTORY}/soundboard`}/>)
    }

    const onFileIsTooLarge = (allowedMegabytes) => {
        setError(`File is too large! Maximum is ${getHumanReadableMaxFileSize(allowedMegabytes)}MB (${allowedMegabytes * MEGABYTE} bytes).`)
    }

    const soundFileChanged = event => {
        const allowedMegabytes = 1
        const file = event.target.files[0]
        if(verifyFileSize(file, allowedMegabytes)) {
            setSoundFile(file)
        } else {
            onFileIsTooLarge(allowedMegabytes)
        }
	}

    const iconFileChanged = event => {
        const allowedMegabytes = 0.5
        if(verifyFileSize(event.target.files[0], allowedMegabytes)) {
            setIconFile(event.target.files[0])
        } else {
            onFileIsTooLarge(allowedMegabytes)
        }
	}

    const onComplete = () => {
        setSubmitting(false)
        setRedirect(true)
    }

    const onError = (err) => {
        if(err.response !== undefined && (err.response.status === 500 || err.response.status === 403)) {
            setError(`${err}: ${err.response.statusText}`)
            setSubmitting(false)
        } else {
            const onUnexpectedError = () => {
                setError("An unexpected error occurred. Please check your internet connection and try again!")
                setSubmitting(false)
            }
            if(err.response === undefined) {
                onUnexpectedError()
            } else {
                err.response.json()
                .then((json)=>{
                    setError(`${err}: ${json.status}`)
                    setSubmitting(false)
                })
                .catch(()=>{
                    onUnexpectedError()
                })
            }
        }
    }

    if(redirect) {
        return(<Redirect to={`${APP_DIRECTORY}/soundboard`}/>)
    } else if(error) {
        return (<ErrorWindow message={error} continue={()=>{setError(undefined)}}/>)
    } else if(submitting) {
        return (<LoadingIndicator message={"Uploading media files..."}/>)
    } else {
        return (<div className="editSlot">
            <h2>Updating slot {slotNumber}</h2>
            <Form.Group controlId="soundFile" className="mb-3">
                <Form.Label>Select sound file (.wav, .mp3)</Form.Label>
                <Form.Control type="file" onChange={soundFileChanged} ref={soundFileRef}/>
            </Form.Group>

            <Form.Group controlId="iconFile" className="mb-3">
                <Form.Label>Select icon file (.png, .jpeg)</Form.Label>
                <Form.Control type="file" onChange={iconFileChanged}/>
            </Form.Group>

            <div className="submitButtons">
                <Button size="md" disabled={soundFile===undefined || iconFile===undefined}
                onClick={()=>{submit(soundFile, iconFile, slotNumber, props.accessToken, onComplete, onError, setSubmitting)}}>
                    Save
                </Button>
                <Button variant="danger" onClick={()=>{setRedirect(true)}}>Cancel</Button>
            </div>
        </div>)
    }
}

export default UploadFile